import React, { useState } from 'react';
import { User } from 'interfaces/user.interface';
import { EventCommitment } from 'interfaces/types.interface';
import EngagementStatusIcons from './engagementStatusIcon.component';

interface EngagementCardProps {
  engagement: EventCommitment;
  engagementIndex: number;
  dayStart?: Date;
  canDrag?: boolean;
  cursor?: string;
  themeHightlighted?: string;
  user?: User;
  rotation?: number;
  isDragging?: boolean;
  isHighlighted?: boolean;
  setIsDragging?: ( dragging: boolean ) => void;
  onEngagementClick?: ( task: EventCommitment ) => void;
  handleDragStart?: (
    e: React.DragEvent<HTMLDivElement>,
    task: EventCommitment,
    dayStartDrag: Date,
    user: User
  ) => void
}

const EngagementSquareCard: React.FC<EngagementCardProps> = ( {
  engagement,
  themeHightlighted,
  engagementIndex,
  dayStart,
  rotation,
  canDrag,
  cursor = 'cursor-grab',
  user,
  isDragging,
  isHighlighted = false,
  setIsDragging,
  onEngagementClick,
  handleDragStart,
} ) => {
  const [ isDraggingSelf, setIsDraggingSelf ] = useState( false );

  const onDragStart = ( e: React.DragEvent<HTMLDivElement> ) => {
    setIsDragging?.( true );
    setIsDraggingSelf( true );
    if ( dayStart && user ) {
      handleDragStart?.( e, engagement, dayStart, user );
    }
  };

  const onDragEnd = () => {
    setIsDragging?.( false );
    setIsDraggingSelf( false );
  };

  const handleClick = ( e: React.MouseEvent<HTMLDivElement> ) => {
    e.stopPropagation();
    onEngagementClick?.( engagement );
  };

  const dragOpacityClass = isDragging
    ? isDraggingSelf
      ? 'opacity-0'
      : 'opacity-50'
    : 'opacity-100';

  const archivedStyle = engagement.archived
    ? 'opacity-60 cursor-not-allowed'
    : '';

  let effectiveIsHighlighted = isHighlighted;
  if ( themeHightlighted !== undefined && themeHightlighted !== null ) {
    effectiveIsHighlighted = themeHightlighted === engagement.color;
  }
  const highlightClass = effectiveIsHighlighted ? 'highlighted-engagement-card-flash' : '';

  return (
    <div
      key={engagementIndex}
      className={`${themeHightlighted && ( themeHightlighted == engagement.color ? 'opacity-100' : 'opacity-40' )}
      trans-fast border-2 flex bg-[#fcffcf] flex-col relative dark:bg-dark-bg-light dark:text-white h-36 w-36 rounded-md shadow-xl ${cursor} ${dragOpacityClass} ${archivedStyle}
      ${isHighlighted ? highlightClass : ''}`}
      onClick={handleClick}
      onDragStart={canDrag ? onDragStart : e => {return e.preventDefault();}}
      onDragEnd={onDragEnd}
      draggable={!!canDrag}
      style={{
        borderColor: engagement.color,
        transition: isHighlighted ? 'none' : 'transform 0.2s ease-in-out',
        transform: !isHighlighted ? `rotate(${rotation}deg)` : undefined,
        '--rotation': `${rotation}deg`,
        zIndex: isHighlighted ? 5 : 1
      } as React.CSSProperties}
    >
      <div className='h-full w-full absolute z-0'/>

      <div className="absolute w-full -top-1 h-2 z-10 flex-center">
        <div
          className="h-4 flex-center bg-white top-2 w-4 rounded-full border-2"
          style={{
            borderColor: engagement.color,
            backgroundColor: engagement.color,
          }}
        />
      </div>

      <p className="h-64 w-32 text-center z-10 break-words mt-4 mx-1">
        <span className="line-clamp-3 leading-[20px]">{engagement.eventTitle}</span>
      </p>

      <EngagementStatusIcons engagement={engagement} />
    </div>
  );
};

export default EngagementSquareCard;
